<template>
	<div class="flex flex-col gap-2">
		<div class="flex justify-between items-center">
			<div class="flex flex-row gap-2">
				<InputGroup style="width: 15rem">
					<InputText id="UserKeywordSearch" v-model="globalFilter" placeholder="Keyword Search" ref="keywordSearchInput" />
					<InputGroupAddon @click="clearGlobalFilter()">
						<div class="flex gap-2 justify-around items-center px-3">
							<i class="pi pi-filter-slash" label="Clear"></i>
						</div>
					</InputGroupAddon>
				</InputGroup>
				<slot name="additionalFilters" />
				<Button icon="pi pi-filter-slash" v-if="filtersApplied" label="Clear Filters" severity="danger" @click="clearAllFilters" />
			</div>

			<div style="text-wrap: nowrap">
				<slot name="additionalButtons"></slot>
			</div>
		</div>

		<DataTable
			:value="tableData"
			:columns="columns"
			v-bind="dataTableProps"
			:loading="loading"
			v-model:filters="filters"
			:row-class="rowClass"
			:globalFilterFields="props.globalFilterFields?.length ? props.globalFilterFields : undefined"
			@rowClick="rowClick"
			ref="dataTableRef"
			@filter="processFilter"
		>
			<template #loading>Loading...</template>
			<template #empty>No data found</template>

			<Column
				v-for="col in columns"
				:key="col.field as string"
				:sortable="col.sortable !== false"
				:field="col.field"
				:sort-field="col.sortField || col.field"
				:filter-field="col.filterField || col.field"
				:filterMenuStyle="{ width: '14rem' }"
				:showFilterMenu="col.filterable !== false"
				:showFilterOperator="false"
				:showFilterMatchModes="col.filterType !== 'dropdown'"
				:showApplyButton="false"
				:showClearButton="false"
				:frozen="col.frozen"
				:alignFrozen="col.alignFrozen"
				:filter="col.filterable !== false"
			>
				<template #header>
					<I2Label :label="col.header || ''" :helpElementName="col.helpElementName" />
				</template>
				<template #body="slotProps">
					<span :style="col.style">
						<template v-if="col.template">
							<div v-html="col.template(slotProps)"></div>
						</template>
						<template v-else>
							{{ slotProps.data[col.field as string] }}
						</template>
					</span>
				</template>
				<template #filter="{ filterModel, filterCallback }">
					<MultiSelect
						v-if="col.filterType === 'dropdown'"
						v-model="filterModel.value"
						:options="withBlankOption(col.filterOptions)"
						optionLabel="label"
						optionValue="value"
						showClear
						filter
						:placeholder="'Select ' + col.header"
						class="p-column-filter"
						@change="filterCallback()"
					/>
					<IconField v-else class="p-column-filter">
						<InputText
							v-model="filterModel.value"
							@input="filterCallback()"
							@keydown.enter="filterCallback()"
							class="w-full"
							:placeholder="'Filter by ' + col.header"
						/>
						<InputIcon
							v-if="filterModel.value"
							class="pi pi-times cursor-pointer"
							@click="filterModel.value = null; filterCallback()"
						/>
					</IconField>
				</template>
			</Column>
			<slot name="extraColumns" />
		</DataTable>
	</div>
</template>

<script setup lang="ts">
import DataTable, { DataTableFilterEvent, DataTableFilterMeta, DataTableProps, DataTableRowClickEvent } from "primevue/datatable"
import Column, { ColumnProps } from "primevue/column"
import Button from "primevue/button"
import InputGroup from "primevue/inputgroup"
import InputGroupAddon from "primevue/inputgroupaddon"
import InputText from "primevue/inputtext"
import MultiSelect from "primevue/multiselect"
import IconField from "primevue/iconfield"
import InputIcon from "primevue/inputicon"
import I2Label from "@/crispify/crispy-components/I2Label.vue"

import { onMounted, PropType, ref, watch, nextTick } from "vue"
import { FilterMatchMode, FilterService } from "@primevue/core/api"

// Sentinel for the synthetic "(Blank)" option in dropdown column filters. PrimeVue's built-in
// "in" matcher treats a null/undefined/"" filter value as "no filter" (matches everything), so
// blank-matching needs its own rule rather than reusing FilterMatchMode.IN with a null sentinel.
const BLANK_FILTER_VALUE = "__ic_blank__"

function withBlankOption(options: { label: string; value: any }[] = []) {
	return [{ label: "(Blank)", value: BLANK_FILTER_VALUE }, ...options]
}

FilterService.register("inWithBlank", (value: any, filter: any[]) => {
	if (!filter || filter.length === 0) return true
	return filter.some((f) => (f === BLANK_FILTER_VALUE ? value === null || value === undefined || value === "" : value === f))
})

const dataTableRef = ref(null)
const keywordSearchInput = ref(null)
const filtersApplied = ref(false)

const props = defineProps({
	columns: {
		type: Array<
			ColumnProps & {
				template?: (props: { data: any; field?: string | ((item: any) => string) }) => any
				style?: string | object
				filterType?: "text" | "dropdown"
				filterOptions?: { label: string; value: any }[]
				filterField?: string
				filterable?: boolean
				// Key into the configurable help-labels store (see I2Label). Optional - a column
				// without one just shows its plain header text with no tooltip/help icon.
				helpElementName?: string
			}
		>,
		required: true,
	},
	loading: { type: Boolean, default: false },
	tableName: { type: String, default: null },
	tableData: { type: Array<Object>, default: () => [], required: true },
	rowClass: { type: Function as PropType<(data: any) => string>, default: null },
	customProps: { type: Object, default: null },
	persistKeywordSearch: { type: Boolean, default: false },
	globalFilterFields: { type: Array as PropType<string[]>, default: undefined },
})

const saveFiltersOnFilterChange = ref(false)
const globalFilter = ref<string | null>("")

const focusKeywordSearch = () => {
	if (keywordSearchInput.value) {
		;(keywordSearchInput.value as any).$el.focus()
	}
}

defineExpose({
	dataTableRef,
	focusKeywordSearch,
})

watch(globalFilter, (newVal) => {
	if (saveFiltersOnFilterChange.value) {
		filters.value["global"] = { value: newVal, matchMode: "contains" }
	}
})

const emit = defineEmits<{
	rowClick: [event: DataTableRowClickEvent]
}>()

const rowClick = (event: DataTableRowClickEvent) => {
	emit("rowClick", event)
}

const filters = defineModel("filters", {
	default: () => ({
		global: { value: null, matchMode: FilterMatchMode.CONTAINS },
	}),
	type: Object as PropType<DataTableFilterMeta>,
})

watch(
	filters,
	(newVal) => {
		if (props.tableName && saveFiltersOnFilterChange.value) {
			if (props.persistKeywordSearch) {
				localStorage.setItem(`${props.tableName}-filters`, JSON.stringify(newVal))
			}
		}
	},
	{ deep: true }
)

const dataTableProps = ref<DataTableProps>({
	rowHover: true,
	loading: props.loading,
	paginator: true,
	rows: 10,
	paginatorTemplate: "CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown",
	rowsPerPageOptions: [5, 10, 20, 50],
	currentPageReportTemplate: "Showing {first} to {last} of {totalRecords}",
	dataKey: "id",
	filters: filters.value,
	filterDisplay: "menu",
	sortMode: "multiple",
	// Frozen columns (position: sticky) only take effect on a scrollable table — PrimeVue's own
	// base CSS scopes the sticky rule under .p-datatable-scrollable. Only opt in when a consumer
	// actually uses a frozen column, so tables that don't stay exactly as they were.
	scrollable: props.columns.some((col) => col.frozen),
})

const clearGlobalFilter = () => {
	globalFilter.value = ""
	Object.assign(filters.value, {
		global: { value: null, matchMode: "contains" },
	})
}

const clearAllFilters = () => {
	initFilters()
}

const initFilters = () => {
	globalFilter.value = ""

	// Initialize filters for each column with proper structure for both row and menu filters
	const newFilters: any = {
		global: { value: null, matchMode: FilterMatchMode.CONTAINS },
	}

	props.columns.forEach((col) => {
		if (col.filterable === false) return
		const matchMode = col.filterType === "dropdown" ? "inWithBlank" : FilterMatchMode.CONTAINS
		// No "constraints"/"operator" here on purpose: with showFilterOperator="false" we only want a
		// single simple filter per column. PrimeVue's fieldConstraints getter (the thing the #filter
		// slot's filterModel actually binds to) resolves to constraints[0] when constraints exists, and
		// falls back to the filter meta itself otherwise. executeLocalFilter, which does the actual
		// matching, always reads the top-level value/matchMode — never constraints. Having both present
		// split the UI (which wrote to constraints[0].value) from the actual filter check (which read the
		// top-level value), so nothing ever visibly filtered no matter what was typed/selected.
		newFilters[(col.filterField || col.field) as string] = {
			value: null,
			matchMode,
		}
	})

	// Replace the entire filters object to ensure clean state
	Object.keys(filters.value).forEach((key) => delete filters.value[key])
	Object.assign(filters.value, newFilters)

	filtersApplied.value = false
}

const filteredData = ref([])

const processFilter = (e: DataTableFilterEvent) => {
	filteredData.value = e.filteredValue
	filtersApplied.value = Object.values(filters.value).some((item) => {
		if (typeof item === "object" && item !== null) {
			// Check direct value for row filters
			if ("value" in item && item.value !== null && item.value !== "") {
				return true
			}
			// Check constraints array for menu filters
			if ("constraints" in item && Array.isArray(item.constraints)) {
				return item.constraints.some((constraint: any) => constraint.value !== null && constraint.value !== "")
			}
		}
		return false
	})
	if (!filtersApplied.value) {
		dataTableProps.value.currentPageReportTemplate = "Showing {first} to {last} of {totalRecords}"
	} else {
		dataTableProps.value.currentPageReportTemplate = `Showing {first} to {last} of {totalRecords} (filtered from ${props.tableData.length})`
	}
}

onMounted(() => {
	initFilters()
	//dataTableProps.value.rows = toInteger(props.rows)
	if (props.tableName && props.persistKeywordSearch) {
		// Load saved filters if we have a table name and persistence is enabled
		const savedFilters = localStorage.getItem(`${props.tableName}-filters`)
		if (savedFilters) {
			Object.assign(filters.value, JSON.parse(savedFilters))
			if (filters.value["global"] && typeof filters.value["global"] === "object" && "value" in filters.value["global"]) {
				globalFilter.value = (filters.value["global"] as any).value
			}
		}
	}
	saveFiltersOnFilterChange.value = true
	Object.assign(dataTableProps.value, props.customProps)

	// Auto-focus the keyword search input when component mounts
	nextTick(() => {
		focusKeywordSearch()
	})
})
</script>

<style scoped></style>
